import { computed, inject } from 'vue';
import { useStore } from 'vuex';

import { DOCUMENT_METADATA, isSuggestedCategory, METADATA } from '~/constants/conversationModels';
import { FILTER_TYPE } from '~/constants/documents';

import {
  GetCategories,
  GetCurrentFields,
  GetCurrentPromptCard,
  GetDefaultPromptFields,
  GetDisplayPromptsInNewTab,
  GetPrompts,
  GetSelectedCategories,
} from '~/constants/getters';
import {
  SetCategories,
  SetCurrentFields,
  SetCurrentPromptCard,
  SetDefaultPromptFields,
  SetDisplayPromptsInNewTab,
  SetPrompts,
  SetPromptsDefaultState,
  SetSelectedCategories,
} from '~/constants/actions';

import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import useFormFilters from '~/composables/useFormFilters';

export default () => {
  const store = useStore();
  const arcaneApiGateway = inject('arcaneApiGateway');
  const { formFilters, setFiltersObject } = useFormFilters();

  const prompts = computed(() => store.getters[`quickPromptsStore/${GetPrompts}`]);
  const currentFields = computed(() => store.getters[`quickPromptsStore/${GetCurrentFields}`]);
  const defaultPromptFields = computed(() => store.getters[`quickPromptsStore/${GetDefaultPromptFields}`]);
  const currentPromptCard = computed(() => store.getters[`quickPromptsStore/${GetCurrentPromptCard}`]);
  const displayPromptsInNewTab = computed(() => store.getters[`quickPromptsStore/${GetDisplayPromptsInNewTab}`]);
  const categories = computed(() => store.getters[`quickPromptsStore/${GetCategories}`]);
  const selectedCategories = computed(() => store.getters[`quickPromptsStore/${GetSelectedCategories}`]);
  const setCurrentFields = (data) => store.dispatch(`quickPromptsStore/${SetCurrentFields}`, data);
  const setDefaultPromptFields = (data) => store.dispatch(`quickPromptsStore/${SetDefaultPromptFields}`, data);
  const setCurrentPromptCard = (data) => store.dispatch(`quickPromptsStore/${SetCurrentPromptCard}`, data);
  const setDisplayPromptsInNewTab = (data) => store.dispatch(`quickPromptsStore/${SetDisplayPromptsInNewTab}`, data);
  const setPrompts = (data) => store.dispatch(`quickPromptsStore/${SetPrompts}`, data);

  const setInitialCardsState = () => store.dispatch(`quickPromptsStore/${SetPromptsDefaultState}`);

  const setCategories = (data) => {
    store.dispatch(`quickPromptsStore/${SetCategories}`, data);
  };

  const setSelectedCategories = (data) => store.dispatch(`quickPromptsStore/${SetSelectedCategories}`, data);

  const onSelectPrompt = async ({ prompt, variablesToFill = [], filtersConfig = [] }) => {
    const { promptData, variables } = await getPromptCardByVersionId({ id: prompt.id, variablesToFill, filtersConfig });
    setInitialFieldsAndPromptCard({ variables, promptData });
    setDisplayPromptsInNewTab(true);
  };

  const getPromptCardsByCategory = async ({ categories, isSuggested }) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.quickPrompts.getPromptCardsByCategory, {
        cardGroupIds: categories || [],
        isSuggested,
      });

      setPrompts(data);

      return data;
    } catch (error) {
      console.log('Failed to get cards', error);
    }
  };

  const fillCurrentVariablesWithData = (variables, variablesData) => {
    if (!variablesData || !variablesData.length) {
      return variables;
    }

    const result = variables.map((variable) => {
      const variableToFill = variablesData.find((v) => v.variable === variable.key);
      if (variableToFill) {
        return {
          ...variable,
          value: variableToFill.value,
        };
      }
      return variable;
    });

    return result;
  };

  const updateProductCountryFilters = () => {
    const productAndCountryFilterValues = currentFields.value.filter(
      (item) =>
        item.variable === DOCUMENT_METADATA.product_name.toLowerCase() ||
        item.variable === DOCUMENT_METADATA.country.toLowerCase(),
    );

    if (productAndCountryFilterValues?.length) {
      const filtersObj = Object.fromEntries(
        productAndCountryFilterValues.map((item) => [`metadata.${item.variable}`, item.value]),
      );

      setFiltersObject({ document: { ...formFilters.value[FILTER_TYPE.document], ...filtersObj } || {} });
    }
  };

  const handleTemplateVariables = (html) => {
    const spanRegex = /<span\b[^>]*>/g;
    const matches = [];
    let spanMatch;

    while ((spanMatch = spanRegex.exec(html)) !== null) {
      const spanTag = spanMatch[0];

      // Extract attributes individually
      const variableMatch = spanTag.match(/data-variable="([^"]*)"/);
      const valueMatch = spanTag.match(/data-value="([^"]*)"/);
      const labelMatch = spanTag.match(/data-label="([^"]*)"/);
      const multipleMatch = spanTag.match(/data-multiple="([^"]*)"/);

      if (!variableMatch || !valueMatch) continue;

      const variable = variableMatch[1];
      const rawValue = valueMatch[1];
      const label = labelMatch ? labelMatch[1] : '';
      const multiple = multipleMatch ? multipleMatch[1] === 'true' : false;

      let value = '';

      if (multiple) {
        value = rawValue
          ? rawValue
              .split(',')
              .map((v) => v.trim())
              .filter((v) => v.length > 0)
          : [];
      } else {
        value = rawValue || '';
      }

      matches.push({ variable, value, multiple, label });
    }

    if (matches.length) {
      setCurrentFields(
        matches.map((item) => ({ variable: item.variable, value: item.value, label: item.label || '' })),
      );

      const hasProductOrCountry = matches.some(
        (m) =>
          m.variable === DOCUMENT_METADATA.product_name.toLowerCase() ||
          m.variable === DOCUMENT_METADATA.country.toLowerCase(),
      );

      if (hasProductOrCountry) {
        updateProductCountryFilters();
      }
    }
  };

  const completeFilterValuesForProductAndCountry = async (variables, isInputDisabled = false) => {
    if (!isInputDisabled) {
      const result = variables.map((variable) => {
        if (variable.key.toLowerCase() === DOCUMENT_METADATA.product_name.toLowerCase()) {
          return {
            ...variable,
            value: formFilters.value[FILTER_TYPE.document]?.[METADATA.product_name],
          };
        }
        if (variable.key.toLowerCase() === DOCUMENT_METADATA.country.toLowerCase()) {
          return {
            ...variable,
            value: formFilters.value[FILTER_TYPE.document]?.[METADATA.country],
          };
        }
        return variable;
      });
      return result;
    }
    return variables;
  };

  const completeOptionsAndFiltersForProductAndCountry = async ({
    variables,
    isInputDisabled = false,
    filtersConfig = [],
  }) => {
    const productAndCountryFilterValues = filtersConfig.filter(
      (item) => item.id === METADATA.product_name || item.id === METADATA.country,
    );
    let result = variables.map((variable) => {
      if (variable.key.toLowerCase() === DOCUMENT_METADATA.product_name.toLowerCase()) {
        return {
          ...variable,
          options: productAndCountryFilterValues.find((item) => item.id === METADATA.product_name)?.optionList || [],
          multiple: true,
        };
      }
      if (variable.key.toLowerCase() === DOCUMENT_METADATA.country.toLowerCase()) {
        return {
          ...variable,
          options: productAndCountryFilterValues.find((item) => item.id === METADATA.country)?.optionList || [],
          multiple: true,
        };
      }
      return variable;
    });
    result = await completeFilterValuesForProductAndCountry(result, isInputDisabled);
    return result;
  };

  const fillTemplateVariables = async ({ variables, variablesToFill, isInputDisabled, filtersConfig }) => {
    let vars = variables;
    if (variablesToFill || currentFields.value.length) {
      vars = fillCurrentVariablesWithData(variables, [...variablesToFill, ...currentFields.value]);
    }

    if (
      vars.find(
        (item) =>
          item.key.toLowerCase() === DOCUMENT_METADATA.product_name.toLowerCase() ||
          item.key.toLowerCase() === DOCUMENT_METADATA.country.toLowerCase(),
      )
    ) {
      vars = await completeOptionsAndFiltersForProductAndCountry({ variables: vars, isInputDisabled, filtersConfig });
    }
    return vars;
  };

  const getPromptCardByVersionId = async ({ id, variablesToFill = [], isInputDisabled, filtersConfig = [] }) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.quickPrompts.getPromptCardByVersionId(id));

      const variables = await fillTemplateVariables({
        variables: data.data,
        variablesToFill,
        isInputDisabled,
        filtersConfig,
      });

      const promptData = { ...data, data: variables };

      return { promptData, variables };
    } catch (error) {
      console.log('Failed to get cards by versionId', error);
    }
  };

  const setInitialFieldsAndPromptCard = ({ variables, promptData }) => {
    setDefaultPromptFields(variables);
    const currentPromptCard = { ...promptData, data: variables };
    setCurrentPromptCard(currentPromptCard);
  };

  const getCategories = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.quickPrompts.getPromptCardsGroups);
      const categoriesData = [isSuggestedCategory, ...data];

      setSelectedCategories([isSuggestedCategory.id]);
      setCategories(categoriesData);

      return categoriesData;
    } catch (error) {
      console.log('Failed to get categories', error);
    }
  };

  return {
    currentFields,
    setCurrentFields,
    currentPromptCard,
    setCurrentPromptCard,
    setInitialCardsState,
    displayPromptsInNewTab,
    setDisplayPromptsInNewTab,
    onSelectPrompt,
    prompts,
    setPrompts,
    categories,
    setCategories,
    getPromptCardsByCategory,
    getPromptCardByVersionId,
    getCategories,
    selectedCategories,
    setSelectedCategories,
    completeFilterValuesForProductAndCountry,
    completeOptionsAndFiltersForProductAndCountry,
    handleTemplateVariables,
    setInitialFieldsAndPromptCard,
    defaultPromptFields,
  };
};
